// Combobox de búsqueda contra un ValueSet de Medplum (Módulo H, Grupo 1) —
// reemplazo shadcn de `ValueSetAutocomplete`/`AsyncAutocomplete`
// (packages/hce-react, Mantine) para los items `open-choice` con
// `answerValueSet` (en la práctica, solo los campos de diagnóstico CIE-10,
// `http://logiacare.ec/fhir/ValueSet/cie10-minimo`). El fetch en sí
// (`medplum.valueSetExpand`) es `@medplum/core` puro, no Mantine — confirmado
// contra `packages/hce-react/src/ValueSetAutocomplete/ValueSetAutocomplete.tsx`.
//
// `buscarEnValueSet` va exportada aparte de la UI para poder testearla sin
// el Popover/Command (mock de MedplumClient).
import type { MedplumClient } from '@medplum/core';
import type { ValueSetExpansionContains } from '@medplum/fhirtypes';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { sinonimosBusquedaCie10, VALUESET_CIE10_MINIMO } from './sinonimosCie10';

export async function buscarEnValueSet(
  medplum: MedplumClient,
  binding: string,
  filtro: string,
  signal?: AbortSignal,
): Promise<ValueSetExpansionContains[]> {
  // CIE-10: además del texto tal cual, el equivalente con el vocabulario oficial ("tipo 2" → "no insulinodependiente").
  const filtros = [filtro, ...(binding === VALUESET_CIE10_MINIMO ? sinonimosBusquedaCie10(filtro) : [])];
  const vistos = new Set<string>();
  const out: ValueSetExpansionContains[] = [];
  for (const f of filtros) {
    const valueSet = await medplum.valueSetExpand({ url: binding, filter: f, count: 20 }, { signal });
    for (const elemento of valueSet.expansion?.contains ?? []) {
      if (elemento.code && !vistos.has(elemento.code)) {
        vistos.add(elemento.code);
        out.push(elemento);
      }
    }
  }
  return out;
}

export interface OpcionValueSet {
  code: string;
  display: string;
  system?: string;
}

export interface ValueSetComboboxProps {
  readonly medplum: MedplumClient;
  readonly binding: string;
  readonly placeholder: string;
  readonly valor: OpcionValueSet | null;
  readonly onChange: (valor: OpcionValueSet | null) => void;
  /** Texto libre que no matcheó ninguna opción del catálogo — permitido, igual que el open-choice original. */
  readonly permitirTextoLibre?: boolean;
}

/** Modo single — un valor, o texto libre si no matchea el catálogo. */
export function ValueSetCombobox({ medplum, binding, placeholder, valor, onChange, permitirTextoLibre = true }: ValueSetComboboxProps): JSX.Element {
  const listboxId = useId();
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [opciones, setOpciones] = useState<ValueSetExpansionContains[]>([]);
  const [cargando, setCargando] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!abierto) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCargando(true);
    const temporizador = setTimeout(() => {
      buscarEnValueSet(medplum, binding, busqueda, controller.signal)
        .then((r) => setOpciones(r))
        .catch(() => setOpciones([]))
        .finally(() => setCargando(false));
    }, 250);
    return () => clearTimeout(temporizador);
  }, [medplum, binding, busqueda, abierto]);

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        {/* oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- patrón shadcn Popover+Command: el trigger es un botón real, no un <input>/<select> nativo. */}
        <Button variant="outline" role="combobox" aria-expanded={abierto} aria-controls={listboxId} className="w-full justify-between font-normal">
          <span className={cn('truncate', !valor && 'text-muted-foreground')}>{valor ? valor.display : placeholder}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeholder} value={busqueda} onValueChange={setBusqueda} />
          <CommandList id={listboxId}>
            <CommandEmpty>
              {cargando
                ? 'Buscando…'
                : permitirTextoLibre && busqueda.trim()
                  ? `Sin coincidencias — se guardará "${busqueda.trim()}" como texto libre.`
                  : 'Sin resultados.'}
            </CommandEmpty>
            <CommandGroup>
              {opciones.map((op) => (
                <CommandItem
                  key={op.code}
                  value={op.code}
                  onSelect={() => {
                    onChange({ code: op.code ?? '', display: op.display ?? op.code ?? '', system: op.system });
                    setAbierto(false);
                  }}
                >
                  <Check className={cn('mr-2 size-4', valor?.code === op.code ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{op.display}</span>
                </CommandItem>
              ))}
              {permitirTextoLibre && busqueda.trim() && (
                <CommandItem
                  value={`__texto-libre__${busqueda}`}
                  onSelect={() => {
                    onChange({ code: '', display: busqueda.trim() });
                    setAbierto(false);
                  }}
                >
                  Usar "{busqueda.trim()}" (texto libre)
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export interface ValueSetComboboxMultiProps {
  readonly medplum: MedplumClient;
  readonly binding: string;
  readonly placeholder: string;
  readonly valores: OpcionValueSet[];
  readonly onChange: (valores: OpcionValueSet[]) => void;
}

/** Modo multi (repeats) — chips + el mismo combobox para agregar uno nuevo. */
export function ValueSetComboboxMulti({ medplum, binding, placeholder, valores, onChange }: ValueSetComboboxMultiProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {valores.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {valores.map((v, i) => (
            <li key={`${v.code}-${i}`} className="flex items-center gap-1 rounded-md border border-border bg-hce-surface px-2 py-1 text-xs">
              <span>{v.display}</span>
              <button
                type="button"
                onClick={() => onChange(valores.filter((_, idx) => idx !== i))}
                className="text-hce-muted hover:text-hce-ink"
                aria-label={`Quitar ${v.display}`}
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <ValueSetCombobox
        medplum={medplum}
        binding={binding}
        placeholder={placeholder}
        valor={null}
        onChange={(nuevo) => {
          if (!nuevo) return;
          onChange([...valores, nuevo]);
        }}
      />
    </div>
  );
}
